// Lightweight API client. Uses relative URLs which work both in dev (proxied via Vite)
// and in production (Worker + Pages on the same origin).

import { readLocal, readSession, KEYS } from './storage.js';
import { asciiHeader } from './identity.js';
import { recordApiError } from './recentErrors.js';
import { APP_VERSION } from './appVersion.js';

// Where the API lives. On the deployed Pages site the app talks to the Worker
// DIRECTLY: Cloudflare's git-triggered Pages builds upload assets only and
// drop the functions/ proxy (verified from the build log on 2026-09-06 — the
// same commit deployed by `wrangler pages deploy` carries it), which took the
// whole app down for an afternoon. The Worker allows the pages.dev origins
// (worker/lib/cors.js), so the proxy is now only a same-origin convenience.
// Local dev keeps /api → the Vite proxy → wrangler dev on :8787.
const WORKER_API = "https://techsheet-api.jasonb-e76.workers.dev/api";
const BASE =
  import.meta.env?.VITE_API_BASE ||
  (typeof window !== "undefined" && /\.pages\.dev$/.test(window.location.hostname) ? WORKER_API : "/api");
function getTechName() {
  return readLocal(KEYS.techName, '');
}

// Header values are ByteStrings: any character above U+00FF — the smart
// apostrophe iOS substitutes into "O'Brien", an emoji, "Michał" — makes
// fetch() throw a TypeError before the request is even sent, bricking every
// API call for that tech with a generic error. Fold smart punctuation to
// ASCII and strip the rest; the tech's typed name stays untouched in storage.
function headerSafeTechName(name) {
  return String(name || '')
    .replace(/[‘’ʼ]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[–—]/g, '-')
    .replace(/ /g, ' ')
    .replace(/[^\x20-\xFF]/g, '')
    .trim()
    // A name written entirely outside Latin-1 sanitizes to nothing — still
    // send an identity so the worker's caller gate doesn't 401 a real tech.
    || (String(name || '').trim() ? 'tech' : '');
}

// The worker checks X-Admin-Pin on exactly these routes. Sending the PIN
// anywhere else leaks it to endpoints that never look at it — and widens the
// surface for a timing probe against the comparison.
function needsAdminPin(path, method) {
  if (path.startsWith('/admin/')) return true;
  if (path === '/brands' && method === 'POST') return true;
  if (/^\/brands\/[^/]+$/.test(path) && method === 'DELETE') return true;
  if (/^\/documents\/[^/]+$/.test(path) && method === 'DELETE') return true;
  return false;
}

// How long a troubleshooting Start or answer may take before the iPad stops
// waiting. The worker's worst case is two 90 s AI calls; past this the send is
// treated as lost and recovered by resending with the same client id
// (src/utils/turnSync.js), which is safe whether or not it landed.
export const TURN_TIMEOUT_MS = 120_000;
// A document upload or scan also carries the file over a weak signal and runs
// the long extraction call (two 90 s attempts at worst), so it gets longer.
export const UPLOAD_TIMEOUT_MS = 240_000;

// `options.timeoutMs` aborts the request after that long; `options.signal`
// (a caller's Cancel) still works alongside it. Either way fetch throws an
// AbortError, which friendlyError words as a timeout.
async function request(path, options = {}) {
  const { timeoutMs, signal: callerSignal, ...fetchOptions } = options;
  options = fetchOptions;
  let signal = callerSignal;
  let timer = null;
  if (timeoutMs) {
    const controller = new AbortController();
    timer = setTimeout(() => controller.abort(), timeoutMs);
    if (callerSignal) {
      if (callerSignal.aborted) controller.abort();
      else callerSignal.addEventListener('abort', () => controller.abort(), { once: true });
    }
    signal = controller.signal;
  }
  try {
    return await sendRequest(path, options, signal);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function sendRequest(path, options, signal) {
  const headers = {
    ...(options.headers || {}),
  };
  // Only set JSON content-type when sending a JSON body. Browsers must set
  // multipart/form-data themselves so they include the boundary.
  if (options.body && typeof options.body === 'string' && !headers['Content-Type']) {
    headers['Content-Type'] = 'application/json';
  }

  const techName = headerSafeTechName(getTechName());
  if (techName) headers['X-Tech-Name'] = techName;

  // The signed-in Google account (D-LAUNCH2) and this build (D-LAUNCH3).
  const techEmail = asciiHeader(readLocal(KEYS.techEmail, ''));
  if (techEmail) headers['X-Tech-Email'] = techEmail;
  headers['X-App-Version'] = asciiHeader(APP_VERSION) || 'dev';

  const method = (options.method || 'GET').toUpperCase();
  if (needsAdminPin(path, method)) {
    const adminPin = readSession(KEYS.adminPin, '');
    if (adminPin) headers['X-Admin-Pin'] = adminPin;
  }

  let res;
  try {
    res = await fetch(`${BASE}${path}`, { ...options, headers, ...(signal ? { signal } : {}) });
  } catch (e) {
    // Network failure: no status. Recorded for the Feedback sheet, then rethrown.
    recordApiError({ method, path, status: 0, message: (e && e.message) || 'Network error' });
    throw e;
  }
  const text = await res.text();
  let data;
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = { raw: text };
  }
  if (!res.ok) {
    recordApiError({ method, path, status: res.status, message: data.error || `Request failed: ${res.status}` });
    const err = new Error(data.error || `Request failed: ${res.status}`);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

export const api = {
  health: () => request('/health'),

  // ---- Data tag ----
  // mode: 'tag' (default) reads the data tag; 'unit' identifies the whole
  // appliance when the tag can't be read (best-guess model via vision + search).
  analyzeTag: ({ image, workOrder, mode }) =>
    request('/analyze-tag', {
      method: 'POST',
      body: JSON.stringify({ image, techName: getTechName(), workOrder, mode }),
      timeoutMs: TURN_TIMEOUT_MS,
    }),

  // ---- Admin: coverage report ----
  getCoverage: (days = 90) => request(`/admin/coverage?days=${days}`),

  // ---- Admin auth ----
  // Returns true if the PIN in sessionStorage matches the worker secret.
  // Lets the Admin page validate up-front instead of waiting until the
  // first save attempt fails with 401.
  checkAdminPin: async () => {
    try {
      await request('/admin/check');
      return true;
    } catch (e) {
      if (e.status === 401) return false;
      throw e;
    }
  },

  // ---- Lookups ----
  listLookups: (params = {}) => {
    const q = new URLSearchParams(params).toString();
    return request(`/lookups${q ? '?' + q : ''}`);
  },
  getLookup: (id) => request(`/lookups/${id}`),
  // Patch one or more fields on a saved lookup. Server re-resolves the
  // manufacturer portal when `brand` is included and returns the merged
  // shape the Result page can swap straight into its `result` object.
  updateLookup: (id, updates) =>
    request(`/lookups/${id}`, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    }),

  // ---- Documents ----
  // Upload a PDF or image. `file` is a File/Blob. `pages` is the optional
  // client-extracted per-page text (src/utils/pdfText.js); the worker indexes
  // it so the troubleshoot AI can retrieve manual pages by symptom.
  uploadDocument: ({ file, modelNumber, docType, lookupId, pages }) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('model_number', modelNumber);
    fd.append('doc_type', docType);
    fd.append('source', 'upload');
    if (lookupId) fd.append('lookup_id', String(lookupId));
    if (getTechName()) fd.append('tech_name', getTechName());
    if (pages?.length) fd.append('pages_json', JSON.stringify({ pages }));
    return request('/documents/upload', { method: 'POST', body: fd, timeoutMs: UPLOAD_TIMEOUT_MS });
  },
  // Page images (migration 0011). PUT one rendered page; GET which pages have one.
  putDocumentPageImage: (docId, pageNumber, blob) =>
    request(`/documents/${docId}/pages/${pageNumber}/image`, {
      method: 'PUT',
      headers: { 'Content-Type': blob.type || 'image/jpeg' },
      body: blob,
    }),
  getDocumentPageImageStatus: (docId) => request(`/documents/${docId}/pages/images`),
  documentPageImageUrl: (docId, pageNumber) => `${BASE}/documents/${docId}/pages/${pageNumber}/image`,
  // Add a tech sheet / wiring diagram by URL. The worker fetches it: a real
  // PDF/image is stored + indexed like an upload; anything else is saved as an
  // openable reference. Returns { id, mode: 'indexed' | 'reference', ... }.
  addDocumentLink: ({ modelNumber, docType, url }) =>
    request('/documents/link', {
      method: 'POST',
      body: JSON.stringify({ model_number: modelNumber, doc_type: docType, url }),
    }),
  // Scan an on-unit doc (camera photo, base64).
  scanDocument: ({ image, modelNumber, lookupId }) =>
    request('/documents/scan', {
      method: 'POST',
      body: JSON.stringify({
        image,
        model_number: modelNumber,
        lookup_id: lookupId || null,
        tech_name: getTechName() || null,
      }),
      timeoutMs: UPLOAD_TIMEOUT_MS,
    }),
  // List all docs saved for a model
  listDocumentsForModel: (model) =>
    request(`/documents/${encodeURIComponent(model)}`),
  // URL of a document file (for inline display / download)
  documentFileUrl: (id) => `${BASE}/documents/file/${id}`,
  // Admin: delete a document
  deleteDocument: (id) =>
    request(`/documents/${id}`, { method: 'DELETE' }),

  // ---- Troubleshoot (conversational) ----
  // Start a new session with the tech's opening complaint. Returns the session
  // id and the AI's first one-step-at-a-time reply.
  // clientSessionId makes a resent Start safe (the worker returns the session
  // the first one created); a 202 { pending } means its first reply is still
  // being written.
  startTroubleshoot: ({ lookupId, modelNumber, brand, applianceType, symptom, clientSessionId, signal }) =>
    request('/troubleshoot/start', {
      method: 'POST',
      body: JSON.stringify({
        lookup_id: lookupId || null,
        model_number: modelNumber,
        brand: brand || null,
        appliance_type: applianceType || null,
        symptom,
        client_session_id: clientSessionId,
      }),
      timeoutMs: TURN_TIMEOUT_MS,
      signal,
    }),
  // Send the tech's answer to the AI's last question; receive the next AI turn.
  // photoDataUrl is an optional "data:image/jpeg;base64,…" string — the
  // worker stores it in R2 and passes it to Claude as multimodal content
  // alongside the text answer (used to verify meter readings).
  // clientTurnId makes a resend safe: the worker returns what it stored for
  // that id (or 202 { pending } while the reply is still being written).
  sendTroubleshootTurn: ({ sessionId, answer, photoDataUrl = null, clientTurnId, signal }) =>
    request(`/troubleshoot/${sessionId}/turn`, {
      method: 'POST',
      body: JSON.stringify({
        answer,
        photo_data_url: photoDataUrl || undefined,
        client_turn_id: clientTurnId,
      }),
      timeoutMs: TURN_TIMEOUT_MS,
      signal,
    }),
  // Mark the session resolved with optional resolution notes.
  endTroubleshoot: ({ sessionId, resolutionNotes }) =>
    request(`/troubleshoot/${sessionId}/end`, {
      method: 'POST',
      body: JSON.stringify({ resolution_notes: resolutionNotes || null }),
    }),
  // Load a session and all its turns (for resume / history transcript view).
  getTroubleshootSession: (sessionId) =>
    request(`/troubleshoot/${sessionId}`),
  // Before a session starts: an unresolved session on this exact model from
  // the last two days (offer to resume it) and prior diagnoses on the family.
  getTroubleshootContext: ({ modelNumber, excludeLookupId = null }) =>
    request(`/troubleshoot/context?model=${encodeURIComponent(modelNumber)}${excludeLookupId ? `&exclude_lookup=${excludeLookupId}` : ''}`),
  // URL of a meter photo attached to a tech turn. Streams from R2 via the
  // worker; cached aggressively (the bytes never change for a given turn).
  // Returns null when the turn has no photo.
  troubleshootPhotoUrl: (turnId) =>
    turnId ? `${BASE}/troubleshoot/photo/${turnId}` : null,

  // ---- Tech Wiki / SOP assistant ----
  // Ask a company-process question grounded in the Notion wiki. `history` is a
  // short rolling context: [{ role: 'tech' | 'assistant', content }]. Returns
  // { answer, cited: [{ page_title, heading }], grounded, signal }.
  askWiki: ({ question, history = [] }) =>
    request('/wiki/ask', {
      method: 'POST',
      body: JSON.stringify({ question, history }),
    }),
  // Most-asked grounded questions, for the Wiki page's suggestion chips.
  wikiTrending: () => request('/wiki/trending'),
  // Admin: crawl the configured Notion pages into wiki_chunks.
  // Returns { pages, chunks, perPage } (or a "Notion not configured" error).
  syncWiki: () => request('/admin/wiki/sync', { method: 'POST' }),
  // Admin: unanswered company questions, grouped, newest first.
  listWikiGaps: () => request('/admin/wiki/gaps'),
  // Admin: mark a gap (and its repeats) resolved.
  resolveWikiGap: (id) =>
    request(`/admin/wiki/gaps/${id}/resolve`, { method: 'POST' }),

  // ---- Tester feedback (D-LAUNCH3) ----
  // A Feedback-button report. `payload` is validated by shared/feedback.js on
  // the worker; `photoBlob` is an optional JPEG/PNG. A stalled request (dead
  // wifi, a worker that never answers) would otherwise leave the tech's
  // sheet showing "Sending…" forever, so this aborts on a timeout — request()
  // spreads its options into fetch, so `signal` rides along, and the abort
  // surfaces as a normal thrown error for friendlyError() to word. A photo
  // upload needs more slack on a weak cell signal than a text-only report,
  // hence the longer budget when one's attached.
  sendFeedback: async ({ payload, photoBlob = null }) => {
    const fd = new FormData();
    fd.append('payload', JSON.stringify(payload));
    if (photoBlob) fd.append('photo', photoBlob, photoBlob.type === 'image/png' ? 'feedback.png' : 'feedback.jpg');
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), photoBlob ? 45000 : 20000);
    try {
      return await request('/feedback', { method: 'POST', body: fd, signal: controller.signal });
    } finally {
      clearTimeout(timeoutId);
    }
  },
  // 👍 = 1, 👎 = -1, clear = 0. One rating per tech per AI turn.
  rateTurn: ({ turnId, rating, category = null, message = null }) =>
    request('/feedback/rating', {
      method: 'POST',
      body: JSON.stringify({ turn_id: turnId, rating, category, message }),
    }),
  // This tech's ratings in one session: { ratings: [{ turn_id, rating, category }] }.
  myRatings: (sessionId) => request(`/feedback/ratings?session_id=${encodeURIComponent(sessionId)}`),
  // A render crash caught by ErrorBoundary.
  reportClientError: ({ message, stack, route }) =>
    request('/errors', { method: 'POST', body: JSON.stringify({ message, stack, route }) }),
};
